/**
 * Normalisation des dates du formulaire de cotation avant envoi en base.
 *
 * Incident du 2026-09-15 (deals Pipedrive 47370, 47371, 47420, 47421, plaque
 * HB-990-AL) : `postDataDB` faisait `dataPayload.reg_date.toString()` sans
 * garde. Quand l'agence saisit le véhicule à la main (plaque inconnue de l'API
 * immat), la date de 1re mise en circulation peut rester vide — `reg_date` vaut
 * alors `null` et l'expression lève un TypeError AVANT le `POST /api/deal`.
 * Résultat : l'affaire existe chez Pipedrive et chez Spark mais jamais chez
 * nous, sans une ligne de log côté serveur (la requête n'est jamais partie).
 *
 * Ces helpers ne lèvent jamais : une date absente ou invalide devient `null`,
 * la cotation part en base et le champ reste simplement vide.
 */

/** `Date`, chaîne ISO ou objet type dayjs — tout ce que le DateField peut produire. */
type DateLike = Date | string | number | { toDate?: () => Date } | null | undefined;

const toDate = (value: DateLike): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.toDate === 'function') return toDate(value.toDate());
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** `YYYY-MM-DD` (fuseau local, pas UTC : une MEC ne doit pas reculer d'un jour). */
export const toIsoDateString = (value: DateLike): string | null => {
  const date = toDate(value);
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** `DD/MM/YYYY`, ou `null` si la date est absente ou invalide. */
export const toFrDateString = (value: DateLike): string | null => {
  const date = toDate(value);
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};
